import { LocalDate } from '@js-joda/core';

import { CompleteAddress } from '@dreryk/address-common-api';
import {
   CompanyInvoiceCreateCommand,
   CurrentHealthcareProviderInvoicePdfQuery,
   ExternalSystemBusinessFault,
   FiscalDocumentProviderAuthorizationFault,
   PaymentMethod,
} from '@dreryk/egabinet-invoice-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';

import { invoiceCreateModalTranslationKeys } from '../translations';

import { InvoiceCreateFormBean } from './form/InvoiceCreateForm.types';
import { getCompanyName, getMedicalServiceName } from './form/servicesSection/InvoiceItemsTable.utils';
import { downloadInvoiceAsPdf } from './queries/queries';
import { invoiceTypeVisitor } from './form/InvoiceCreateForm.utils';

type CompanyInvoiceCreateCommandOptions = {
   onSuccess: VoidFunction;
   paymentMethod: PaymentMethod;
   paymentMethodDescription: string | undefined;
   visitDate: LocalDate;
   visitSettlementId: number;
   receiptAvailableForInvoice: boolean;
};

export const useCompanyInvoiceCreateCommand = (
   options: CompanyInvoiceCreateCommandOptions
): UseCqrsCommandResult<
   void,
   InvoiceCreateFormBean,
   FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault
> =>
   useCqrsCommand<void, InvoiceCreateFormBean, FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault>(
      (parameters) => {
         const command = new CompanyInvoiceCreateCommand();
         const completeAddress = CompleteAddress.builder()
            .withCountry(parameters.country)
            .withTown(parameters.town!)
            .withPostcode(parameters.postcode!)
            .withStreet(parameters.street)
            .withBuilding(parameters.building!)
            .withFlat(parameters.flat)
            .build();

         command.address = completeAddress;
         command.companyName = getCompanyName(parameters.companyName);
         command.description = Choice.withUseDefaultIfNull(parameters.description);
         command.fiscalItems = parameters.invoiceItems.map((item) => ({
            name: item.invoiceParameters.nameForInvoicing ?? getMedicalServiceName(item.name)!,
            quantity: item.quantity!,
            unit: item.unit!,
            unitPrice: Money.of(item.unitPrice!),
            taxRate: item.taxRate!,
         }));
         command.paymentMethod = options.paymentMethod;
         command.paymentMethodDescription = Choice.withUseDefaultIfNull(options.paymentMethodDescription ?? null);
         command.paymentTo = parameters.paymentDate;
         command.sellDate = options.visitDate;
         command.taxExemptionBasis = parameters.taxExemptionBasis;
         command.taxIdentifier = parameters.taxIdentifier;
         command.referenceId = options.visitSettlementId.toString();

         invoiceTypeVisitor(options.receiptAvailableForInvoice)({
            withReceipt: () => {
               command.receiptReferenceId = Choice.withUseDefaultIfNull(options.visitSettlementId.toString());
            },
            default: () => null,
         });

         return command;
      },
      {
         onSuccess: async () => {
            dispatchSnackbarPositiveEvent(invoiceCreateModalTranslationKeys.invoiceCreateSuccess);
            await downloadInvoiceAsPdf(options.visitSettlementId.toString())();
            refetchCqrsQuery(CurrentHealthcareProviderInvoicePdfQuery);
            options.onSuccess();
         },
         onFault: {
            'egabinet-invoice-frontend-api:FiscalDocumentProviderAuthorizationFault': () => {
               dispatchSnackbarNegativeEvent(invoiceCreateModalTranslationKeys.apiTokenFault);
            },
            'egabinet-invoice-frontend-api:ExternalSystemBusinessFault': ({ externalSystemErrorMessage }) => {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(invoiceCreateModalTranslationKeys.externalSystemBusinessFault, {
                     messageFromFakturownia: externalSystemErrorMessage,
                  })
               );
            },
         },
      }
   );
